import {
  createDiffHunkReferencesFromEntries,
  isSuggestionCommitMessageGenerationEligible,
} from '@github-ui/diff-lines/helpers/create-diff-hunk-references-from-entries'
import type {SuggestionGenerationConfig} from '@github-ui/diff-lines/hooks/use-generate-suggestion-commit-message'
import type {CommitMessageGenerationConfig, DiffEntry} from '@github-ui/diff-lines/conversation-types'
import type {MarkersInfo} from '../stores/slices/markers'
import type {BatchedSuggestion} from '../stores/slices/suggestion-batch'
import {isCommittableSuggestion, type UnifiedBatchItem} from '../stores/slices/unified-batch'
import {buildCommentIndex} from './get-suggestion-details'
import {getThreadIsOutdated} from './unified-batch-items'

export const MAX_BATCH_DIFF_CHARACTERS = 64_000

type BatchContext = {
  authorization: CommitMessageGenerationConfig | null | undefined
  pullRequestPath: string
  headOid: string
}

type GenerationItem = {
  id: number
  eligible: boolean
  diffEntries?: DiffEntry[]
}

function createConfig(items: GenerationItem[], context: BatchContext): SuggestionGenerationConfig | undefined {
  const {authorization, pullRequestPath, headOid} = context
  if (!authorization?.apiUrl || !headOid || items.length === 0) return

  const diffHunks: SuggestionGenerationConfig['diffHunks'] = []
  const sortedItems = [...items].sort((a, b) => a.id - b.id)
  let characters = 0
  for (const item of sortedItems) {
    if (!item.eligible) return
    const hunks = createDiffHunkReferencesFromEntries(item.diffEntries ?? [])
    // Never describe only a subset of the changes that will be committed.
    if (hunks.length === 0) return
    for (const hunk of hunks) {
      characters += hunk.data.diff.length + hunk.data.fileName.length
      if (characters > MAX_BATCH_DIFF_CHARACTERS) return
      diffHunks.push(hunk)
    }
  }

  return {
    batchKey: JSON.stringify([pullRequestPath, headOid, sortedItems.map(item => item.id), diffHunks, authorization]),
    batchSize: items.length,
    diffHunks,
    basePath: authorization.apiUrl,
    ssoOrgIds: authorization.ssoOrganizations.map(org => org.id),
  }
}

export function createUnifiedBatchCommitMessageConfig(
  items: UnifiedBatchItem[],
  context: BatchContext,
): SuggestionGenerationConfig | undefined {
  return createConfig(
    items.map(item => ({
      id: item.commentDatabaseId,
      eligible:
        isCommittableSuggestion(item) &&
        item.kind === 'ccr-with-suggestion' &&
        item.commitMessageGenerationEligible === true,
      diffEntries: item.kind === 'ccr-with-suggestion' ? item.suggestionFiles : undefined,
    })),
    context,
  )
}

export function createLegacyBatchCommitMessageConfig(
  suggestions: BatchedSuggestion[],
  markers: MarkersInfo | undefined,
  context: BatchContext,
): SuggestionGenerationConfig | undefined {
  if (!context.authorization) return
  const index = buildCommentIndex(markers)
  return createConfig(
    suggestions.map(suggestion => {
      const found = index.get(suggestion.commentDatabaseId)
      return {
        id: suggestion.commentDatabaseId,
        eligible:
          suggestion.suggestionIndex === undefined &&
          Boolean(found && !getThreadIsOutdated(found.thread)) &&
          isSuggestionCommitMessageGenerationEligible(found?.comment.automatedComment),
        diffEntries: found?.comment.automatedComment?.suggestion?.diffEntries,
      }
    }),
    context,
  )
}
